import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';


const SERVER_ORIGIN = process.env.REACT_APP_SERVER_ORIGIN

export const useDashBaseAPI = (prefix) => {
  const { getAccessTokenSilently } = useAuth0();
  
  const instance = axios.create({
    baseURL: SERVER_ORIGIN + prefix,
    headers: {
      "Content-Type": "application/json"
    }
  });
    
  // Register in "axios API instance" pipeline (i.e. .use() similar to registering request handler in express pipieline) so that this block of code runs dynamically before every API request for this instance goes out
  instance.interceptors.request.use(async (config) => {
    try {
      const token = await getAccessTokenSilently();
      config.headers.Authorization = `Bearer ${token}`;
      //console.log("[SUCCESS]:", config.headers.Authorization)

    } catch (error) {
      // If token retrieval fails (e.g., request to Auth server responds with error: login_required), silently catch error and return null as value for authorization header
      // Keeps framework consistent: backend will handle directions for how front end should behave based on back end response, so contniue to keep backend as single source of truth for API error directions to front end
      config.headers.Authorization = null; 
      console.log("[ERROR]:", config.headers.Authorization)
      
    }

    return config; // config is function parameter, so it's in entire function scope (meaning changes to config object are accessible outside try/catch blocks as well)

  });

  // takes two callback functions as args - one for sucess and one for error: axios.interceptors.response.use(successCallback, errorCallback);
  instance.interceptors.response.use(
    // Call back 1 in response interceptor: if response is a response and not an error, just return response (i.e. call pseudo "next" with response) so respnse gets passed through to next in psuedo-pipeline to handle response
    (response) => {
      // No error, just pass through
      return response;
    },

    // Call back 2 in response interceptor: if reponse if an error, check for specific error to handle in specific way where we would possible end execution of pipeline, instead of calling psuedo-next and passing error along to next "step controller" in pseudo-pipeline to handle error
    (error) => {
      // a) if error code is a 401 (unauthorized) or 403 (Forbidden), we know error response generated from the checkJWT middlewear in pipeline on backend
      // b) and therefore all silent attempts get valid AT for authorization header by code run in getAccessTokenSilently() for user have been exhausted and unsuccessful (i.e. invalid or no AT for user making request)
      // c) and therefore need user to log back in the long way to get fresh ATs, so redirect to login page
      // d) and we wrap it in a conditional because if the request never makes it to the backend because some connection issue, we wont have a response prop on the error object
        // meaning doing .status on an undefined property (which we have to do to see if this is an unathorized request signified by the status code) would blow up the app and cause it to crash!
        // so this guard is also implicitly saying that we only do the routing if this request actually made it to the backend and therefore the response prop is on the error object; note: status codes also dont exist, whether on the response object or not, for a network error)
      if (error.response?.status === 401) {
        // Redirect to login
        window.location.href = '/';
      }
      // If an error but not specific code we want to specially handle in this step controller, pass (pseudo-next call) error along to next step controller in pipeline to handle error there
      return Promise.reject(error);
    }
  );
  
  return instance;
};
